import React, { useContext, useState } from 'react';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import ButtonV2 from 'common/ui/ButtonV2';
import SingleSelect from 'common/ui/SingleSelect';
import numberWithCommas from 'common/util/numberWithCommas';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import styles from 'css-module/components/subdomain/admin/billing/_PlanTierLimitLineItem.module.scss';

import { LineItem } from './BillingCards';

import type { FullBillingData, TieredBillingPlan } from 'common/api/endpoints/companies';

function getRecommendedPriceTier(
  plan: TieredBillingPlan,
  planTierLimit: FullBillingData['planTierLimit'],
  contributorCount: number
) {
  const currentPriceTier = plan.priceTiers.find((tier) => {
    return tier.users === planTierLimit?.users;
  });
  const minimumPriceTier = plan.priceTiers.find((tier) => {
    return tier.users >= contributorCount;
  });
  const lastPriceTier = plan.priceTiers[plan.priceTiers.length - 1];
  return currentPriceTier || minimumPriceTier || lastPriceTier;
}

interface Props {
  billingData: FullBillingData;
  contributorCount: number;
}
interface DispatchProps extends Props {
  reloadCompany: () => Promise<void>;
}
const _PlanTierLimitLineItem = ({
  billingData,
  contributorCount,
  reloadCompany,
}: DispatchProps) => {
  const showToast = useContext(ShowToastContext);
  const [isSettingLimit, setIsSettingLimit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { plan, planTierLimit } = billingData;
  const [selectedTier, setSelectedTier] = useState<string | null>(
    planTierLimit?.users ? planTierLimit?.users.toString() : null
  );

  const noLimitOption = {
    label: 'No limit',
    value: 'no-limit',
  };
  const priceOptions = plan?.priceTiers
    ? plan.priceTiers.map((tier) => {
        const timeframe = plan.timeframe === 'monthly' ? 'mo' : 'yr';
        return {
          label: `$${numberWithCommas(tier.price / 100)}/${timeframe} (${numberWithCommas(
            tier.users
          )} tracked users)`,
          value: String(tier.users),
        };
      })
    : [];
  const options = [noLimitOption, ...priceOptions];

  const selectedOption = options.find((option) => {
    return option.value === selectedTier;
  });

  if (!plan.tiered || !plan.priceTiers) {
    return null;
  }

  const onSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const response = await AJAX.post('/api/billing/setPlanTierLimit', {
      planTierLimit:
        selectedTier === 'no-limit'
          ? null
          : {
              users: Number(selectedTier),
            },
    });

    const parsedResponse = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (parsedResponse.error) {
      showToast('Something went wrong, please try again later', ToastTypes.error);
    } else {
      await reloadCompany();
      setIsSettingLimit(false);
      showToast('Limit updated', ToastTypes.success);
    }

    setIsSaving(false);
  };

  if (isSettingLimit) {
    return (
      <LineItem
        label="Monthly spend limit"
        value={
          <div className={styles.planTierLimitValue} data-is-setting-limit={isSettingLimit}>
            <SingleSelect
              isItemDisabled={(option) => {
                if (option.value === 'no-limit') {
                  return false;
                }
                const users = Number(option.value);
                return users < contributorCount;
              }}
              onChange={(option) => {
                if (!option || option.value === 'no-limit') {
                  setSelectedTier('no-limit');
                } else {
                  setSelectedTier(option.value);
                }
              }}
              options={options}
              value={selectedOption}
            />
            <ButtonV2 loading={isSaving} size="small" onClick={onSave}>
              Save
            </ButtonV2>
          </div>
        }
      />
    );
  } else {
    const priceTier = plan.priceTiers.find((tier) => {
      return tier.users === planTierLimit?.users;
    });
    return (
      <LineItem
        label="Monthly spend limit"
        value={
          <div className={styles.planTierLimitValue}>
            {priceTier ? (
              <>
                ${numberWithCommas(priceTier.price / 100)}/mo ({numberWithCommas(priceTier.users)}{' '}
                tracked users)
              </>
            ) : (
              'No limit'
            )}
            <button
              className={styles.planTierLimitSetButton}
              onClick={() => {
                const priceTier = getRecommendedPriceTier(plan, planTierLimit, contributorCount);
                setSelectedTier(priceTier.users.toString());
                setIsSettingLimit(true);
              }}>
              {planTierLimit?.users ? 'Change' : 'Set limit'}
            </button>
          </div>
        }
      />
    );
  }
};
const PlanTierLimitLineItem = connect(null, { reloadCompany })(
  _PlanTierLimitLineItem
) as unknown as React.FC<Props>;

export default PlanTierLimitLineItem;
